import { FC, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Image,
  Input,
  QRCode,
  Row,
  Select,
  Space,
  Typography
} from 'antd';
import { useOrganizationsStateContext } from 'providers/Organizations';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import config from 'config';

import { PageWrapper } from 'components/ui';
import { downloadFileFromUrl, getFileUrl } from 'utils/file';
import useCopyText from 'hooks/useCopyText';
import { validateUTM } from 'utils/validation';

const PartnerLinkBuilder: FC = () => {
  const [form] = Form.useForm();

  const { copyText } = useCopyText();

  const { results } = useOrganizationsStateContext();

  const [link, setLink] = useState('');

  const downloadCanvasQRCode = () => {
    const canvas = document
      .getElementById('partner-link-qr-code')
      ?.querySelector<HTMLCanvasElement>('canvas');

    if (canvas) {
      const url = canvas.toDataURL();

      const utmSource = form.getFieldValue('utm_source');

      const organization = results.find(item => item.value === utmSource);

      downloadFileFromUrl(
        url,
        `partner_${organization?.label?.toLowerCase() || ''}_link.png`
      );
    }
  };

  const onResetForm = () => {
    form.resetFields();
    setLink('');
  };

  const onFinish = (values: Record<string, string>) => {
    const searchParams = new URLSearchParams();

    if (values) {
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
    }

    setLink(`${config.website_url}?${searchParams.toString()}`);
  };

  const onCopyLink = () => {
    copyText(link);
  };

  return (
    <PageWrapper title="Link Builder" color="white" back>
      <Row gutter={30}>
        <Col span={16}>
          <Form layout="vertical" size="large" onFinish={onFinish} form={form}>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  label="Source"
                  name="utm_source"
                  rules={[{ required: true }]}
                >
                  <Select disabled={!!link}>
                    {results
                      .filter(item => item.value !== 'all')
                      .map(item => (
                        <Select.Option
                          key={`organization-${item.value}`}
                          value={item.value}
                        >
                          <Space align="center" size={8}>
                            {!!item.icon && (
                              <Image
                                src={getFileUrl(item.icon)}
                                width={24}
                                height={24}
                                preview={false}
                              />
                            )}
                            <Typography.Text>{item.label}</Typography.Text>
                          </Space>
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Medium"
                  name="utm_medium"
                  rules={[{ required: true }, { validator: validateUTM }]}
                >
                  <Input maxLength={255} disabled={!!link} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Campaign"
                  name="utm_campaign"
                  rules={[{ required: true }, { validator: validateUTM }]}
                >
                  <Input maxLength={255} disabled={!!link} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Term"
                  name="utm_term"
                  rules={[{ validator: validateUTM }]}
                >
                  <Input maxLength={255} disabled={!!link} />
                </Form.Item>
              </Col>
              {link ? (
                <Col span={24}>
                  <Form.Item label="Link">
                    <div style={{ position: 'relative' }}>
                      <Input.TextArea
                        value={link}
                        style={{
                          resize: 'none',
                          paddingRight: 36,
                          height: 150
                        }}
                      />
                      <Button
                        type="text"
                        icon={<CopyOutlined style={{ color: '#1677ff' }} />}
                        onClick={onCopyLink}
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          zIndex: 2
                        }}
                      />
                    </div>
                  </Form.Item>
                </Col>
              ) : (
                <Col span={24}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Generate Link and QR
                    </Button>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
          {!!link && (
            <Button onClick={onResetForm} size="large">
              Reset Generated Content
            </Button>
          )}
        </Col>
        <Col span={8}>
          <Card
            style={{ background: '#FBFBFB', opacity: link ? 1 : 0.5 }}
            title="QR View"
          >
            <Flex
              id="partner-link-qr-code"
              justify="center"
              align="center"
              gap={40}
              vertical
            >
              <QRCode value={link || config.website_url || ''} size={210} />
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadCanvasQRCode}
                disabled={!link}
              >
                Download QR
              </Button>
            </Flex>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default PartnerLinkBuilder;
